import { User } from '@auth0/auth0-react'
import { USER_TYPES } from '@editor/config/constants'
import { UserInfo } from '@editor/interfaces/user'
import { DefaultEditorDisplayConfig } from '@editor/pages/projects/editor/constants'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { setUser as setSentryUser } from '@sentry/react'

export interface UserState {
  /**
   * Whether the user has been loaded (both from Auth0 and from the DB).
   */
  userLoaded: boolean

  /**
   * Auth0 user object.
   */
  user: User

  /**
   * User profile from DB.
   */
  userProfile: UserInfo | null

  /**
   * User type from DB.
   */
  userType: string

  /**
   * Whether the user has a deprecated subscription.
   */
  hasDeprecatedSubscription: boolean | null

  /**
   * Whether the user is allowed to use debug features.
   */
  isAllowedToDebug: boolean
}

const initialState: UserState = {
  userLoaded: false,
  user: {},
  userProfile: null,
  userType: USER_TYPES.NON_PAYING_USER,
  hasDeprecatedSubscription: null,
  isAllowedToDebug: false,
}

export const slice = createSlice({
  name: 'page',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      setSentryUser({
        id: action.payload.sub,
        email: action.payload.email,
        'Email Domain': action.payload.email?.split('@')[1],
      })
    },
    setUserProfile: (state, { payload }: PayloadAction<UserInfo>) => {
      state.userProfile = {
        ...payload,
        display_config: payload.display_config || DefaultEditorDisplayConfig,
      }
    },
    setUserType: (state, action: PayloadAction<string>) => {
      state.userType = action.payload
    },
    setUserLoaded: (state) => {
      state.userLoaded = true
    },
    setHasDeprecatedSubscription: (state, action: PayloadAction<boolean>) => {
      state.hasDeprecatedSubscription = action.payload
    },
    setIsAllowedToDebug: (state, action: PayloadAction<boolean>) => {
      state.isAllowedToDebug = action.payload
    },
  },
})

export const {
  setUser,
  setUserProfile,
  setUserType,
  setUserLoaded,
  setHasDeprecatedSubscription,
  setIsAllowedToDebug,
} = slice.actions
export default slice.reducer
